export const DOC_INTEL_POLL_MS = 2500;

export const UPLOAD_STATUS = {
  QUEUED: 'queued',
  EXTRACTING: 'extracting',
  REVIEW_PENDING: 'review_pending',
  APPROVED: 'approved',
  APPLIED: 'applied',
  FAILED: 'failed',
  DISCARDED: 'discarded'
};

export function uploadNeedsPolling(status) {
  return status === UPLOAD_STATUS.QUEUED || status === UPLOAD_STATUS.EXTRACTING;
}

export function getUploadId(upload) {
  return upload?.uploadId ?? upload?.id ?? null;
}

export function getReviewSessionId(upload) {
  return (
    upload?.reviewSessionId ??
    upload?.latestReview?.id ??
    null
  );
}

export function getPanelForUpload(upload) {
  if (!upload) return 'empty';
  switch (upload.status) {
    case UPLOAD_STATUS.QUEUED:
    case UPLOAD_STATUS.EXTRACTING:
      return 'processing';
    case UPLOAD_STATUS.REVIEW_PENDING:
      return 'review';
    case UPLOAD_STATUS.APPROVED:
      return 'mapping';
    case UPLOAD_STATUS.APPLIED:
      return 'success';
    case UPLOAD_STATUS.FAILED:
      return 'failed';
    default:
      return 'empty';
  }
}

export function getStatusBadge(status) {
  switch (status) {
    case UPLOAD_STATUS.QUEUED:
      return { label: 'Queued', className: 'bg-slate-100 text-slate-600' };
    case UPLOAD_STATUS.EXTRACTING:
      return { label: 'Reading…', className: 'bg-sky-50 text-sky-800' };
    case UPLOAD_STATUS.REVIEW_PENDING:
      return { label: 'Review', className: 'bg-amber-50 text-amber-800' };
    case UPLOAD_STATUS.APPROVED:
      return { label: 'Approved', className: 'bg-teal-50 text-teal-800' };
    case UPLOAD_STATUS.APPLIED:
      return { label: 'Applied', className: 'bg-emerald-50 text-emerald-800' };
    case UPLOAD_STATUS.FAILED:
      return { label: 'Failed', className: 'bg-red-50 text-red-700' };
    default:
      return { label: status || '—', className: 'bg-slate-100 text-slate-600' };
  }
}

export function formatRelativeTime(date) {
  if (!date) return '';
  const diffMs = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatFileSize(bytes) {
  if (bytes == null || bytes === '') return null;
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function groupFieldsBySection(fields = []) {
  const groups = {};
  for (const field of fields) {
    const parts = (field.canonical_name || '').split('.');
    const section =
      parts.length >= 2 && /^\d+$/.test(parts[1])
        ? `${parts[0]}.${parts[1]}`
        : parts[0] || 'other';
    const label =
      section.startsWith('applicant.')
        ? section === 'applicant' || section === 'applicant.1'
          ? 'Primary applicant'
          : `Co-applicant ${section.split('.')[1]}`
        : section.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    if (!groups[label]) groups[label] = [];
    groups[label].push(field);
  }
  return groups;
}

export function confidenceTone(confidence) {
  const c = Number(confidence ?? 1);
  if (c < 0.75) return 'text-red-600';
  if (c < 0.85) return 'text-amber-700';
  return 'text-slate-500';
}

export const TARGET_TYPE_LABELS = {
  application_column: 'Application column',
  dynamic_field: 'Dynamic field',
  static: 'Static',
  custom_json: 'Custom JSON'
};

export const WORKFLOW_PHASES_CORE = [
  { id: 'processing', label: 'Upload' },
  { id: 'review', label: 'Review' },
  { id: 'mapping', label: 'Map' },
  { id: 'success', label: 'Apply' }
];

export const WORKFLOW_PHASE_TEMPLATE = { id: 'template', label: 'Template' };

/** @deprecated use getPhaseSteps */
export const WORKFLOW_PHASES = WORKFLOW_PHASES_CORE;

export function getActivePhaseIndex(panel, { includeTemplate = false } = {}) {
  const map = includeTemplate
    ? { processing: 0, review: 1, mapping: 2, success: 3, template: 4, failed: 0 }
    : { processing: 0, review: 1, mapping: 2, success: 3, failed: 0 };
  return map[panel] ?? 0;
}

export function getPhaseSteps(panel, { includeTemplate = false } = {}) {
  const phases = includeTemplate
    ? [...WORKFLOW_PHASES_CORE, WORKFLOW_PHASE_TEMPLATE]
    : WORKFLOW_PHASES_CORE;
  const activeIndex = getActivePhaseIndex(panel, { includeTemplate });

  return phases.map((phase, index) => ({
    ...phase,
    state:
      panel === 'failed' && index === 0
        ? 'active'
        : index < activeIndex
          ? 'done'
          : index === activeIndex
            ? 'active'
            : 'pending'
  }));
}

export function extractPlaceholderNames(content = '') {
  const names = new Set();
  const re = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;
  let match = re.exec(content);
  while (match) {
    names.add(match[1]);
    match = re.exec(content);
  }
  return [...names];
}

export function getPanelMeta(panel, upload) {
  const name = upload?.original_filename || 'Document';
  switch (panel) {
    case 'processing':
      return {
        title: upload?.status === 'extracting' ? 'Reading document' : 'Queued for extraction',
        subtitle: name,
        hint: 'AI is pulling text and fields — usually takes a few seconds.'
      };
    case 'review':
      return {
        title: 'Review extracted fields',
        subtitle: name,
        hint: 'Fix low-confidence values, then approve to unlock mapping.'
      };
    case 'mapping':
      return {
        title: 'Map to CRM',
        subtitle: name,
        hint: 'Confirm where each value lands on the deal before applying.'
      };
    case 'success':
      return {
        title: 'Applied to deal',
        subtitle: name,
        hint: 'Download a fresh agreement draft to confirm mapped KYC fields.'
      };
    case 'failed':
      return {
        title: 'Extraction failed',
        subtitle: name,
        hint: upload?.error_message || 'Try a clearer scan or a different file format.'
      };
    case 'template':
      return {
        title: 'Agreement template',
        subtitle: name,
        hint: 'Edit content. Placeholders auto-fill from the deal after apply.'
      };
    default:
      return { title: 'Smart upload', subtitle: null, hint: null };
  }
}

export function humanizeFieldName(canonicalName) {
  const leaf = (canonicalName || '').split('.').pop() || canonicalName;
  return leaf.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getFileVisual(name, mimeType) {
  const ext = (name?.split('.').pop() || '').toLowerCase();
  const mime = (mimeType || '').toLowerCase();

  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) || mime.startsWith('image/')) {
    return { tone: 'violet', label: 'IMG' };
  }
  if (['pdf'].includes(ext) || mime === 'application/pdf') {
    return { tone: 'rose', label: 'PDF' };
  }
  if (['txt'].includes(ext) || mime.startsWith('text/')) {
    return { tone: 'sky', label: 'TXT' };
  }
  return { tone: 'slate', label: 'DOC' };
}

const FILE_TONE_CLASSES = {
  violet: 'bg-violet-50 text-violet-700 ring-violet-100',
  rose: 'bg-rose-50 text-rose-700 ring-rose-100',
  sky: 'bg-sky-50 text-sky-700 ring-sky-100',
  slate: 'bg-slate-100 text-slate-600 ring-slate-200/80'
};

export function getFileVisualClasses(name, mimeType) {
  const { tone, label } = getFileVisual(name, mimeType);
  return { className: FILE_TONE_CLASSES[tone] || FILE_TONE_CLASSES.slate, label };
}

export function isPreviewableImage(mimeType, filename) {
  const mime = (mimeType || '').toLowerCase();
  const ext = (filename?.split('.').pop() || '').toLowerCase();
  return mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext);
}
